import React from "react";
import DataManagerProductSelect from "./DataManagerProductSelect.jsx";
import BillToSelect from "./BillToSelect.jsx";
import { LoaderCircle, Plus, Save, X } from "lucide-react";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import {
  ASPIRATOR_SELECT_OPTIONS,
  ORDER_LOADING_PRIORITY_OPTIONS,
  ORDER_RATE_OVERRIDE_HELP,
  PRELOADED_PREVIOUS_DAY_SELECT_OPTIONS,
  coerceLoadingClientDraftValue,
  getCreateAddressValidationKey,
  getProductCodeSuggestions,
  normalizeAspiratorDraftValue,
  normalizeDeliveryPeriodDraftValue,
  normalizeOntarioDraftValue,
  normalizeLoadingPriorityDraftValue,
  normalizePreloadedPreviousDayDraftValue,
} from "../hooks/useDataManagerOrdersUtils.js";
import { HoverCard } from "./ResultsPrimitives.jsx";
import {
  Field,
  LoadingAddressHoverCard,
  LoadingClientAutocompleteInput,
  PickerInput,
  handleEditableGridKeyDown,
  preventAddressInputDragEvent,
} from "./DataManagerOrdersPrimitives.jsx";

const PRIMARY_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1 rounded-full bg-accent px-2.75 text-[11px] font-semibold text-white transition-colors duration-150 hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.75 text-[11px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60";
const INPUT_ERROR_CLASS_NAME = "border-rose-300 bg-rose-50/70 text-rose-800 placeholder:text-rose-300 focus:border-rose-400";
const CREATE_FORM_INPUT_CLASS_NAME = "h-7 w-full rounded-[8px] border border-slate-200 bg-white px-2 text-[11px] font-normal text-slate-700 outline-hidden transition-colors duration-150 placeholder:text-slate-400 focus:border-accent focus:bg-white focus:text-slate-800 disabled:cursor-not-allowed disabled:bg-white disabled:text-slate-500";
const CREATE_FORM_PRIORITY_SELECT_CLASS_NAME = CREATE_FORM_INPUT_CLASS_NAME;
const CREATE_FORM_PICKER_INPUT_CLASS_NAME = "h-7 w-full appearance-none rounded-[8px] border border-slate-200 bg-white pl-2 pr-6 text-[11px] font-normal text-slate-700 outline-hidden transition-colors duration-150 focus:border-accent focus:bg-white focus:text-slate-800 disabled:cursor-not-allowed disabled:bg-white disabled:text-slate-500 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0";
const CREATE_FORM_PICKER_BUTTON_CLASS_NAME = "absolute inset-y-0 right-0 flex w-6 items-center justify-center rounded-r-[8px] border-l border-slate-200 bg-white text-slate-500 transition-colors duration-150 hover:text-slate-700 disabled:cursor-not-allowed disabled:text-slate-300";
const CREATE_FORM_SECTION_CLASS_NAME = "grid min-w-0 gap-1 rounded-[4px] border border-[#d6cec5] bg-[#eee9e3] px-2 py-1.5";
const CREATE_FORM_SECTION_TITLE_CLASS_NAME = "text-sm font-extrabold uppercase tracking-[0.14em] text-[#39312a]";
const CREATE_FORM_FIELD_LABEL_CLASS_NAME = "flex min-h-[16px] items-center text-[10px] font-semibold uppercase leading-[16px] tracking-[0.06em] text-[#494039]";
const CREATE_FORM_TONNE_RATE_FIELD_LABEL_CLASS_NAME = `${CREATE_FORM_FIELD_LABEL_CLASS_NAME} whitespace-nowrap`;
const CREATE_FORM_TONNE_RATE_INPUT_FILLED_CLASS_NAME = "border-amber-300/80 bg-amber-50 text-amber-950";
const CREATE_FORM_READONLY_INPUT_CLASS_NAME = `${CREATE_FORM_INPUT_CLASS_NAME} border-slate-200 text-slate-600`;
const CREATE_FORM_DELIVERY_ROW_CLASS_NAME = "grid gap-1 rounded-[4px] border border-[#d6cec5] bg-transparent p-2";
const CREATE_FORM_SAVE_BUTTON_DISABLED_CLASS_NAME = "pointer-events-none cursor-not-allowed opacity-60";
const EXPANDED_ADDRESS_INPUT_CLASS_NAME = "focus:relative focus:z-20 focus:w-[24rem] focus:max-w-none focus:shadow-[0_12px_28px_rgba(15,23,42,0.16)]";

function CreateFormField({ className = "", error = "", labelClassName = CREATE_FORM_FIELD_LABEL_CLASS_NAME, ...props }) {
  return <Field className={`${className} ${error ? "data-manager-create-field-error" : ""}`.trim()} error={error} labelClassName={labelClassName} {...props} />;
}

function stripTonneRateInputSuffix(value) {
  return String(value ?? "").replace(/\s*\$\/[th]\s*$/i, "").trim().replace(",", ".");
}

function TonneRateInput({ hasError = false, onValueChange, value, unit = "$/t", label, help }) {
  const trimmedValue = String(value ?? "").trim();
  const hasValue = trimmedValue !== "";

  return (
    <div
      className={[
        "inline-flex h-6 w-full min-w-0 items-center gap-1 rounded-[4px] border px-2 py-0 text-[11px] transition-colors duration-150 focus-within:border-accent focus-within:bg-white",
        hasError
          ? `${INPUT_ERROR_CLASS_NAME} focus-within:border-rose-400`
          : hasValue
            ? `${CREATE_FORM_TONNE_RATE_INPUT_FILLED_CLASS_NAME} focus-within:text-slate-800`
            : "border-slate-200 bg-white text-slate-700 focus-within:text-slate-800",
      ].join(" ")}
    >
      <input
        aria-invalid={hasError || undefined}
        aria-label={label}
        title={help}
        className="min-w-0 border-0 bg-transparent p-0 text-[11px] font-normal text-inherit outline-hidden placeholder:text-slate-400"
        inputMode="decimal"
        onChange={(event) => onValueChange(stripTonneRateInputSuffix(event.target.value))}
        placeholder={unit}
        style={hasValue ? { width: `${Math.max(trimmedValue.length, 1)}ch` } : { width: "100%" }}
        type="text"
        value={trimmedValue}
      />
      {hasValue ? (
        <span className="shrink-0 text-[10px] font-medium text-slate-500">
          {unit}
        </span>
      ) : null}
    </div>
  );
}

function CreateOrderSaveDisabledHoverCardContent({ messages = [] }) {
  if (messages.length <= 1) {
    return <span>{messages[0] || ""}</span>;
  }

  return (
    <ul className="grid list-disc gap-1 pl-4">
      {messages.map((message) => (
        <li key={message}>{message}</li>
      ))}
    </ul>
  );
}

function getCreateInputClassName(hasError) {
  return hasError ? `${CREATE_FORM_INPUT_CLASS_NAME} ${INPUT_ERROR_CLASS_NAME}` : CREATE_FORM_INPUT_CLASS_NAME;
}

function getCompactCreateInputClassName(hasError) {
  return `${getCreateInputClassName(hasError).replace("h-7", "h-6").replace("rounded-[8px]", "rounded-[4px]")} py-0`;
}

function getAddressEditInputClassName(baseClassName, isAddressField = false) {
  return isAddressField ? `${baseClassName} ${EXPANDED_ADDRESS_INPUT_CLASS_NAME}` : baseClassName;
}

function getVisibleCreateDeliveryFieldError(visibleNewOrderErrors, deliveryIndex, fieldKey) {
  return visibleNewOrderErrors[`deliveries.${deliveryIndex}.${fieldKey}`] || "";
}

function RemoveDeliveryConfirmPopover({
  clientName = "",
  disabled = false,
  onConfirm,
}) {
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const popoverRef = React.useRef(null);
  const popoverId = React.useId();
  const trimmedClientName = typeof clientName === "string" ? clientName.trim() : "";

  usePopoverDismiss({
    anchorElement: anchorRef.current,
    floatingRef: popoverRef,
    isOpen: isConfirmOpen,
    onClose: () => setIsConfirmOpen(false),
  });

  const handleOpenConfirm = () => {
    if (disabled) {
      return;
    }

    setIsConfirmOpen(true);
  };

  const handleConfirm = () => {
    onConfirm?.();
    setIsConfirmOpen(false);
  };

  return (
    <div ref={anchorRef} className="relative">
      <button
        type="button"
        aria-controls={isConfirmOpen ? popoverId : undefined}
        aria-expanded={isConfirmOpen}
        aria-haspopup="dialog"
        onClick={handleOpenConfirm}
        disabled={disabled}
        className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors duration-150 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
        title="Retirer ce déchargement"
      >
        <X size={12} />
      </button>

      {isConfirmOpen ? (
        <div
          id={popoverId}
          ref={popoverRef}
          role="alertdialog"
          aria-labelledby={`${popoverId}-title`}
          aria-describedby={`${popoverId}-description`}
          className="absolute right-0 top-[calc(100%+0.35rem)] z-popover w-56 rounded-lg border border-[rgba(148,163,184,0.18)] bg-white/98 p-2.5 shadow-[0_22px_44px_rgba(15,23,42,0.14)] backdrop-blur-md motion-safe:animate-popover-enter"
          onMouseDown={(event) => event.preventDefault()}
        >
          <p id={`${popoverId}-title`} className="text-[11px] font-semibold text-slate-900">
            Retirer ce déchargement ?
          </p>
          <p id={`${popoverId}-description`} className="mt-1 text-[10px] leading-4 text-slate-600">
            {trimmedClientName
              ? `La livraison pour « ${trimmedClientName} » sera retirée du brouillon.`
              : "Cette livraison sera retirée du brouillon."}
          </p>
          <div className="mt-2 flex justify-end gap-1.5">
            <button
              type="button"
              onClick={() => setIsConfirmOpen(false)}
              className="inline-flex h-7 items-center rounded-full border border-slate-200 bg-white px-2.5 text-[10px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="inline-flex h-7 items-center rounded-full border border-rose-200 bg-rose-600 px-2.5 text-[10px] font-semibold text-white transition-colors duration-150 hover:bg-rose-700"
            >
              Retirer
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function handleCreateFormKeyDown(event) {
  if (event.defaultPrevented || event.key !== "Enter" || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
    return;
  }

  if (event.nativeEvent?.isComposing) {
    return;
  }

  const tagName = typeof event.target?.tagName === "string" ? event.target.tagName.toUpperCase() : "";
  if (tagName === "BUTTON" || tagName === "TEXTAREA") {
    return;
  }

  const isCreateAddressField = typeof event.target?.closest === "function"
    ? event.target.closest("[data-create-address-field='true']")
    : null;
  if (isCreateAddressField) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
}

export default function DataManagerOrdersCreatePanel({
  canAddOrder = true,
  toolbar = null,
  productCatalog = [],
  productCodes = [],
  canCreate = false,
  createAddressValidationPending = false,
  createBusy = false,
  createDisabledMessages = [],
  createDeliveryCount = 1,
  createLoadingAddressPreview = null,
  createOrderDeliveries = [],
  createPending = false,
  isCreatingOrder = false,
  loadingClientOptions = [],
  loadingClientOptionsByLookupKey = {},
  newOrderDraft = null,
  registerAddressValidationFieldRef = null,
  visibleNewOrderErrors = {},
  addNewOrderDelivery,
  handleCreateAddressEnterValidation,
  handleOpenCreateOrderDialog,
  handleStartCreatingOrder,
  handleStopCreatingOrder,
  removeNewOrderDelivery,
  updateNewOrderDeliveryValue,
  updateNewOrderDraftValue,
}) {
  const isSaveDisabled = createBusy || !canCreate;
  const showSaveDisabledHoverCard = isSaveDisabled && createDisabledMessages.length > 0;
  const saveButton = (
    <button
      type="button"
      aria-disabled={isSaveDisabled ? "true" : undefined}
      disabled={isSaveDisabled && !showSaveDisabledHoverCard}
      onClick={showSaveDisabledHoverCard ? undefined : handleOpenCreateOrderDialog}
      className={[
        PRIMARY_ACTION_CLASS_NAME,
        showSaveDisabledHoverCard ? CREATE_FORM_SAVE_BUTTON_DISABLED_CLASS_NAME : "",
      ].join(" ").trim()}
    >
      {createBusy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Save size={14} />}
      {createAddressValidationPending ? "Validation..." : createPending ? "Sauvegarde..." : "Sauvegarder"}
    </button>
  );
  const saveAction = showSaveDisabledHoverCard ? (
    <HoverCard
      accentColor="var(--hovercard-accent-warning)"
      content={<CreateOrderSaveDisabledHoverCardContent messages={createDisabledMessages} />}
      delay={80}
      maxWidth={360}
      placement="bottom-end"
      title="Sauvegarde impossible"
    >
      <span className="inline-flex cursor-not-allowed">{saveButton}</span>
    </HoverCard>
  ) : saveButton;

  return (
    <section className={isCreatingOrder ? "grid gap-1.5" : ""}>
      <div className={`data-manager-tabs-actions flex flex-wrap justify-between ${isCreatingOrder ? "items-start gap-2.5" : "items-center gap-2"}`}>
        <div className="min-w-0 flex-1 [&>div:first-child]:mb-0">
          {toolbar}
        </div>

        {isCreatingOrder ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={handleStopCreatingOrder}
              disabled={createBusy}
              className={SECONDARY_ACTION_CLASS_NAME}
            >
              Fermer
            </button>
            {saveAction}
          </div>
        ) : canAddOrder ? (
          <button
            type="button"
            onClick={handleStartCreatingOrder}
            disabled={createBusy}
            className={PRIMARY_ACTION_CLASS_NAME}
          >
            <Plus size={14} />
            Ajouter une commande
          </button>
        ) : null}
      </div>

      {isCreatingOrder ? (
        <form
          className="data-manager-create-order-form grid gap-1.25 rounded-lg border border-slate-200 bg-white px-2 py-1.5"
          onKeyDown={handleCreateFormKeyDown}
          onSubmit={(event) => {
            event.preventDefault();
            handleOpenCreateOrderDialog?.();
          }}
        >
          <fieldset disabled={createBusy} className="grid gap-1.25">
            <div className={CREATE_FORM_SECTION_CLASS_NAME}>
              <div className="flex flex-wrap items-center justify-between gap-1.5">
                <h4 className={CREATE_FORM_SECTION_TITLE_CLASS_NAME}>Chargement</h4>
                {createLoadingAddressPreview ? (
                  <span className="max-w-full truncate text-[10px] font-medium text-slate-200" title={createLoadingAddressPreview}>
                    {createLoadingAddressPreview}
                  </span>
                ) : null}
              </div>

              <div className="grid gap-1 md:grid-cols-2 xl:grid-cols-[8rem_minmax(0,13rem)_minmax(0,15rem)_minmax(0,7rem)_minmax(0,7rem)_minmax(0,18rem)_7rem_7rem_7rem] xl:items-start">
                <CreateFormField error={visibleNewOrderErrors.delivery_date} label="Date livraison" required>
                  <PickerInput
                    buttonClassName={CREATE_FORM_PICKER_BUTTON_CLASS_NAME}
                    inputClassName={visibleNewOrderErrors.delivery_date
                      ? `${CREATE_FORM_PICKER_INPUT_CLASS_NAME} ${INPUT_ERROR_CLASS_NAME}`
                      : CREATE_FORM_PICKER_INPUT_CLASS_NAME}
                    onChange={(event) => updateNewOrderDraftValue("delivery_date", event.target.value)}
                    placeholder="jj-mm-aaaa"
                    required
                    selectDayOnFocus
                    type="date"
                    value={newOrderDraft.delivery_date}
                  />
                </CreateFormField>

                <CreateFormField
                  className="relative overflow-visible"
                  error={visibleNewOrderErrors.loading_client_name}
                  label="Client chargement"
                  required
                >
                  <LoadingAddressHoverCard address={createLoadingAddressPreview}>
                    <div className="min-w-0">
                      <LoadingClientAutocompleteInput
                        className={getCreateInputClassName(Boolean(visibleNewOrderErrors.loading_client_name))}
                        error={visibleNewOrderErrors.loading_client_name}
                        listId="create-order-loading-client"
                        onBlur={() => {
                          const resolvedValue = coerceLoadingClientDraftValue(newOrderDraft.loading_client_name, loadingClientOptionsByLookupKey);
                          if (resolvedValue !== newOrderDraft.loading_client_name) {
                            updateNewOrderDraftValue("loading_client_name", resolvedValue);
                          }
                        }}
                        onValueChange={(nextValue) => updateNewOrderDraftValue("loading_client_name", nextValue)}
                        options={loadingClientOptions}
                        placeholder="Nom client"
                        value={coerceLoadingClientDraftValue(newOrderDraft.loading_client_name, loadingClientOptionsByLookupKey)}
                      />
                    </div>
                  </LoadingAddressHoverCard>
                </CreateFormField>

                <CreateFormField label="Adresse chargement">
                  <LoadingAddressHoverCard address={createLoadingAddressPreview}>
                    <div className="min-w-0">
                      <input
                        className={CREATE_FORM_READONLY_INPUT_CLASS_NAME}
                        placeholder="Renseignée depuis le client de chargement"
                        readOnly
                        type="text"
                        value={createLoadingAddressPreview || ""}
                      />
                    </div>
                  </LoadingAddressHoverCard>
                </CreateFormField>

                <CreateFormField label="Veille">
                  <select
                    className={CREATE_FORM_INPUT_CLASS_NAME}
                    onChange={(event) => updateNewOrderDraftValue("is_preloaded_previous_day", event.target.value)}
                    value={normalizePreloadedPreviousDayDraftValue(newOrderDraft.is_preloaded_previous_day)}
                  >
                    {PRELOADED_PREVIOUS_DAY_SELECT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </CreateFormField>

                <CreateFormField label="Soir">
                  <select
                    className={CREATE_FORM_INPUT_CLASS_NAME}
                    onChange={(event) => updateNewOrderDraftValue("delivery_period", event.target.value)}
                    value={normalizeDeliveryPeriodDraftValue(newOrderDraft.delivery_period)}
                  >
                    {PRELOADED_PREVIOUS_DAY_SELECT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </CreateFormField>

                <CreateFormField error={visibleNewOrderErrors.coop} label="Facturer à" required>
                  <BillToSelect
                    allowAucun
                    className={getCompactCreateInputClassName(Boolean(visibleNewOrderErrors.coop))}
                    onChange={(value) => updateNewOrderDraftValue("coop", value)}
                    value={newOrderDraft.coop ?? ""}
                  />
                </CreateFormField>

                <CreateFormField
                  error={visibleNewOrderErrors.hourly_rate_override}
                  label="Exception $/h"
                  labelClassName={CREATE_FORM_TONNE_RATE_FIELD_LABEL_CLASS_NAME}
                >
                  <TonneRateInput
                    label="Exception $/h"
                    unit="$/h"
                    help={ORDER_RATE_OVERRIDE_HELP.hourly}
                    hasError={Boolean(visibleNewOrderErrors.hourly_rate_override)}
                    onValueChange={(nextValue) => updateNewOrderDraftValue("hourly_rate_override", nextValue)}
                    value={newOrderDraft.hourly_rate_override}
                  />
                </CreateFormField>

                <CreateFormField
                  error={visibleNewOrderErrors.loading_tonne_rate_override}
                  label="Exception $/t"
                  labelClassName={CREATE_FORM_TONNE_RATE_FIELD_LABEL_CLASS_NAME}
                >
                  <TonneRateInput
                    label="Exception $/t"
                    help={ORDER_RATE_OVERRIDE_HELP.tonne}
                    hasError={Boolean(visibleNewOrderErrors.loading_tonne_rate_override)}
                    onValueChange={(nextValue) => updateNewOrderDraftValue("loading_tonne_rate_override", nextValue)}
                    value={newOrderDraft.loading_tonne_rate_override}
                  />
                </CreateFormField>

                <CreateFormField
                  error={visibleNewOrderErrors.fixed_rate_override}
                  label="Exception $ fixe"
                  labelClassName={CREATE_FORM_TONNE_RATE_FIELD_LABEL_CLASS_NAME}
                >
                  <TonneRateInput
                    label="Exception $ fixe"
                    unit="$"
                    help="Vide : aucun montant, sauf une Aspiration qui reçoit le tarif de Facturation. 0 : aucun montant. Une valeur positive s’ajoute au montant horaire."
                    hasError={Boolean(visibleNewOrderErrors.fixed_rate_override)}
                    onValueChange={(nextValue) => updateNewOrderDraftValue("fixed_rate_override", nextValue)}
                    value={newOrderDraft.fixed_rate_override}
                  />
                </CreateFormField>

              </div>
            </div>

            <div className={CREATE_FORM_SECTION_CLASS_NAME}>
              <div className="flex flex-wrap items-center justify-between gap-1.5">
                <h4 className={CREATE_FORM_SECTION_TITLE_CLASS_NAME}>Déchargement</h4>
                <button
                  type="button"
                  onClick={addNewOrderDelivery}
                  className="inline-flex h-7 items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 text-[10px] font-semibold text-slate-800 transition-colors duration-150 hover:border-slate-400 hover:text-slate-950"
                >
                  <Plus size={12} />
                  Ajouter livraison
                </button>
              </div>

              <div className="grid gap-1">
                {createOrderDeliveries.map((delivery, index) => {
                  const deliveryAspiratorError = getVisibleCreateDeliveryFieldError(visibleNewOrderErrors, index, "aspirator");
                  const deliveryOntarioError = getVisibleCreateDeliveryFieldError(visibleNewOrderErrors, index, "ontario");
                  const deliveryClientError = getVisibleCreateDeliveryFieldError(visibleNewOrderErrors, index, "client_name");
                  const deliveryAddressError = getVisibleCreateDeliveryFieldError(visibleNewOrderErrors, index, "address");
                  const deliveryGrainCodeError = getVisibleCreateDeliveryFieldError(visibleNewOrderErrors, index, "grain_code");
                  const deliveryGrainTypeError = getVisibleCreateDeliveryFieldError(visibleNewOrderErrors, index, "grain_type");
                  const deliveryPriorityError = getVisibleCreateDeliveryFieldError(visibleNewOrderErrors, index, "delivery_priority");
                  const deliveryQuantityError = getVisibleCreateDeliveryFieldError(visibleNewOrderErrors, index, "quantity_tons");

                  return (
                    <div
                      key={delivery.draft_id}
                      className={CREATE_FORM_DELIVERY_ROW_CLASS_NAME}
                    >
                      <div
                        className={[
                          "grid gap-1 md:grid-cols-2 xl:items-start",
                          createDeliveryCount > 1
                            ? "xl:grid-cols-[7rem_6.5rem_minmax(0,11rem)_minmax(0,20rem)_2rem]"
                            : "xl:grid-cols-[7rem_6.5rem_minmax(0,11rem)_minmax(0,20rem)]",
                        ].join(" ")}
                      >
                        <CreateFormField label="Bon commande">
                          <input
                            className={CREATE_FORM_READONLY_INPUT_CLASS_NAME}
                            disabled
                            type="text"
                            value=""
                          />
                        </CreateFormField>

                        <CreateFormField label="Bon travail">
                          <input
                            className={CREATE_FORM_INPUT_CLASS_NAME}
                            onChange={(event) => updateNewOrderDeliveryValue(delivery.draft_id, "work_order_number", event.target.value)}
                            placeholder="OT-2026-001"
                            type="text"
                            value={delivery.work_order_number}
                          />
                        </CreateFormField>

                        <CreateFormField
                          className="relative overflow-visible"
                          error={deliveryClientError}
                          label="Client déchargement"
                          required
                        >
                          <div className="min-w-0">
                            <LoadingClientAutocompleteInput
                              className={getCreateInputClassName(Boolean(deliveryClientError))}
                              error={deliveryClientError}
                              listId={`create-order-delivery-client-${delivery.draft_id}`}
                              onValueChange={(nextValue) => updateNewOrderDeliveryValue(delivery.draft_id, "client_name", nextValue)}
                              options={loadingClientOptions}
                              placeholder="Client"
                              value={delivery.client_name}
                            />
                          </div>
                        </CreateFormField>

                        <CreateFormField className="relative overflow-visible" error={deliveryAddressError} label="Adresse" required>
                          <input
                            className={getAddressEditInputClassName(getCreateInputClassName(Boolean(deliveryAddressError)), true)}
                            data-create-address-field="true"
                            draggable={false}
                            ref={(node) => registerAddressValidationFieldRef?.(getCreateAddressValidationKey(delivery.draft_id, "address"), node)}
                            onChange={(event) => updateNewOrderDeliveryValue(delivery.draft_id, "address", event.target.value)}
                            onDragOver={preventAddressInputDragEvent}
                            onDragStart={preventAddressInputDragEvent}
                            onDrop={preventAddressInputDragEvent}
                            onKeyDown={(event) => handleEditableGridKeyDown(event, { onSubmit: () => handleCreateAddressEnterValidation(delivery.draft_id, "address") })}
                            placeholder="Adresse de déchargement"
                            required
                            type="text"
                            value={delivery.address}
                          />
                        </CreateFormField>

                        {createDeliveryCount > 1 ? (
                          <div className="flex items-start justify-end xl:pt-4.5">
                            <RemoveDeliveryConfirmPopover
                              clientName={delivery.client_name}
                              disabled={createBusy || createDeliveryCount <= 1}
                              onConfirm={() => removeNewOrderDelivery(delivery.draft_id)}
                            />
                          </div>
                        ) : null}

                      </div>

                      <div className="grid gap-1 md:grid-cols-2 xl:grid-cols-[7rem_7rem_minmax(0,11rem)_5rem_6rem_4.5rem_5rem_minmax(0,8rem)_minmax(0,9rem)_4.5rem_minmax(0,1fr)] xl:items-start">
                        <CreateFormField error={deliveryGrainTypeError} label="Produit" required>
                          <DataManagerProductSelect
                            products={productCatalog}
                            className={getCreateInputClassName(Boolean(deliveryGrainTypeError))}
                            onChange={(value) => updateNewOrderDeliveryValue(delivery.draft_id, "grain_type", value)}
                            value={delivery.grain_type}
                          />
                        </CreateFormField>

                        <CreateFormField error={deliveryGrainCodeError} label="Code produit" required>
                          <LoadingClientAutocompleteInput
                            className={getCompactCreateInputClassName(Boolean(deliveryGrainCodeError))}
                            error={deliveryGrainCodeError}
                            getSuggestions={getProductCodeSuggestions}
                            listId={`create-order-product-code-${delivery.draft_id}`}
                            options={productCodes}
                            suggestionsClassName="rounded-[4px] max-w-[calc(100vw-1rem)]"
                            onValueChange={(value) => updateNewOrderDeliveryValue(delivery.draft_id, "grain_code", value)}
                            required
                            value={delivery.grain_code}
                          />
                        </CreateFormField>

                        <CreateFormField error={deliveryAspiratorError} label="Aspirateur">
                          <select
                            className={getCreateInputClassName(Boolean(deliveryAspiratorError))}
                            onChange={(event) => updateNewOrderDeliveryValue(delivery.draft_id, "aspirator", event.target.value)}
                            value={normalizeAspiratorDraftValue(delivery.aspirator)}
                          >
                            {ASPIRATOR_SELECT_OPTIONS.map((option) => (
                              <option key={option.value || "empty"} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </CreateFormField>

                        <CreateFormField error={deliveryOntarioError} label="Ontario">
                          <select
                            className={getCreateInputClassName(Boolean(deliveryOntarioError))}
                            onChange={(event) => updateNewOrderDeliveryValue(delivery.draft_id, "ontario", event.target.value)}
                            value={normalizeOntarioDraftValue(delivery.ontario)}
                          >
                            {PRELOADED_PREVIOUS_DAY_SELECT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </CreateFormField>

                        <CreateFormField error={deliveryQuantityError} label="Nb tm" required>
                          <input
                            className={getCreateInputClassName(Boolean(deliveryQuantityError))}
                            inputMode="decimal"
                            min="0"
                            onChange={(event) => updateNewOrderDeliveryValue(delivery.draft_id, "quantity_tons", event.target.value)}
                            required
                            step="0.1"
                            type="number"
                            value={delivery.quantity_tons}
                          />
                        </CreateFormField>

                        <CreateFormField label="Dompeur">
                          <select
                            className={CREATE_FORM_INPUT_CLASS_NAME}
                            onChange={(event) => updateNewOrderDraftValue("is_dompeur", event.target.value)}
                            value={normalizePreloadedPreviousDayDraftValue(newOrderDraft.is_dompeur)}
                          >
                            {PRELOADED_PREVIOUS_DAY_SELECT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </CreateFormField>

                        <CreateFormField label="Bâtiment">
                          <input
                            className={CREATE_FORM_INPUT_CLASS_NAME}
                            onChange={(event) => updateNewOrderDeliveryValue(delivery.draft_id, "building", event.target.value)}
                            placeholder="Bâtiment"
                            type="text"
                            value={delivery.building}
                          />
                        </CreateFormField>

                        <CreateFormField label="Silo">
                          <input
                            className={CREATE_FORM_INPUT_CLASS_NAME}
                            onChange={(event) => updateNewOrderDeliveryValue(delivery.draft_id, "loading_silo", event.target.value)}
                            type="text"
                            value={delivery.loading_silo}
                          />
                        </CreateFormField>

                        <CreateFormField label="Début livr.">
                          <PickerInput
                            buttonClassName={CREATE_FORM_PICKER_BUTTON_CLASS_NAME}
                            inputClassName={CREATE_FORM_PICKER_INPUT_CLASS_NAME}
                            onChange={(event) => updateNewOrderDeliveryValue(delivery.draft_id, "delivery_window_start", event.target.value)}
                            step="60"
                            type="time"
                            value={delivery.delivery_window_start}
                          />
                        </CreateFormField>

                        <CreateFormField error={deliveryPriorityError} label="Priorité">
                          <select
                            className={deliveryPriorityError ? `${CREATE_FORM_PRIORITY_SELECT_CLASS_NAME} ${INPUT_ERROR_CLASS_NAME}` : CREATE_FORM_PRIORITY_SELECT_CLASS_NAME}
                            onChange={(event) => updateNewOrderDeliveryValue(delivery.draft_id, "delivery_priority", event.target.value)}
                            value={normalizeLoadingPriorityDraftValue(delivery.delivery_priority)}
                          >
                            {ORDER_LOADING_PRIORITY_OPTIONS.map((option) => (
                              <option key={option.value || "empty"} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </CreateFormField>

                        <CreateFormField label="Commentaire" className="md:col-span-2 xl:col-span-1">
                          <input
                            className={CREATE_FORM_INPUT_CLASS_NAME}
                            onChange={(event) => updateNewOrderDeliveryValue(delivery.draft_id, "comment", event.target.value)}
                            placeholder="Commentaire"
                            type="text"
                            value={delivery.comment}
                          />
                        </CreateFormField>

                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </fieldset>
        </form>
      ) : null}
    </section>
  );
}
